import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Trash2 } from 'lucide-react';
import { photosApi, type PhotoDirectory } from '../../api/photos.api';
import { useAuthStore } from '../../store/auth.store';
import { useTeamPhoto } from '../../views/lessons/agenda/instructorPhoto';
import { Avatar, Button, Menu, cx, initials, type AvatarSize, type AvatarTint } from '../charte';
import { squarePhoto } from './squarePhoto';

// Photos de profil dans l'application du bureau. Un seul annuaire par école (`/v1/photos`) donne
// l'adresse versionnée de chaque photo ; les images sont chargées à la demande et gardées en
// mémoire (`useTeamPhoto`). Sans photo, pendant le chargement ou en cas d'échec : les initiales.

export const PHOTO_DIRECTORY_KEY = 'photo-directory';
const EMPTY: PhotoDirectory = { candidates: {}, staff: {}, instructors: {} };

const addresses = (value: unknown): Record<string, string> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(
        Object.entries(value).filter(
          (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].startsWith('/v1/'),
        ),
      )
    : {};

/** Annuaire tel que reçu ; une réponse inattendue vaut un annuaire vide (initiales partout). */
export function photoDirectory(data: unknown): PhotoDirectory {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return EMPTY;
  const raw = data as Partial<Record<keyof PhotoDirectory, unknown>>;
  return { candidates: addresses(raw.candidates), staff: addresses(raw.staff), instructors: addresses(raw.instructors) };
}

/** Annuaire des photos de l'école courante, relu toutes les cinq minutes au plus. */
export function usePhotoDirectory(): PhotoDirectory {
  const schoolId = useAuthStore((s) => s.activeSchoolId);
  const userId = useAuthStore((s) => s.user?.id);
  const { data } = useQuery({
    queryKey: [PHOTO_DIRECTORY_KEY, schoolId, userId],
    queryFn: photosApi.directory,
    select: photoDirectory,
    enabled: !!userId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data ?? EMPTY;
}

/** Image prête à afficher de la photo d'un élève, ou `undefined` (initiales). */
export function useCandidatePhoto(candidateId?: string | null): string | undefined {
  const directory = usePhotoDirectory();
  return useTeamPhoto(candidateId ? directory.candidates[candidateId] : null);
}

/** Image de la photo d'un compte de l'équipe (moniteur relié à sa fiche). */
export function useStaffPhoto(userId?: string | null): string | undefined {
  const directory = usePhotoDirectory();
  return useTeamPhoto(userId ? directory.staff[userId] : null);
}

/** Image de la photo d'un moniteur désigné par son identifiant de planning. */
export function useInstructorPhoto(instructorId?: string | null): string | undefined {
  const directory = usePhotoDirectory();
  return useTeamPhoto(instructorId ? directory.instructors[instructorId] : null);
}

interface PersonAvatarProps {
  name: string;
  size?: AvatarSize;
  tint?: AvatarTint;
  className?: string;
}

/** Avatar d'un élève : sa photo, sinon ses initiales. */
export function CandidateAvatar({ candidateId, ...props }: PersonAvatarProps & { candidateId?: string | null }) {
  return <Avatar {...props} src={useCandidatePhoto(candidateId)} />;
}

/** Avatar d'un compte de l'équipe : la photo de sa fiche de moniteur, sinon ses initiales. */
export function StaffAvatar({ userId, ...props }: PersonAvatarProps & { userId?: string | null }) {
  return <Avatar {...props} src={useStaffPhoto(userId)} />;
}

/** Avatar d'un moniteur du planning. */
export function InstructorAvatar({ instructorId, ...props }: PersonAvatarProps & { instructorId?: string | null }) {
  return <Avatar {...props} src={useInstructorPhoto(instructorId)} />;
}

/** Photo seule d'un élève, posée sur des initiales (pastilles dessinées sur place) ; rien sans photo. */
export function CandidatePhoto({ candidateId, className }: { candidateId?: string | null; className?: string }) {
  const src = useCandidatePhoto(candidateId);
  return src ? <img src={src} alt="" className={className} /> : null;
}

/** Photo seule d'un compte de l'équipe, posée sur des initiales ; rien sans photo. */
export function StaffPhoto({ userId, className }: { userId?: string | null; className?: string }) {
  const src = useStaffPhoto(userId);
  return src ? <img src={src} alt="" className={className} /> : null;
}

/**
 * Contenu d'une pastille dessinée sur place : la photo, sinon les initiales (ou le texte donné).
 * La pastille doit couper ce qui dépasse (`overflow-hidden`).
 */
export function PhotoOrInitials({ src, name, fallback }: { src?: string; name: string; fallback?: ReactNode }) {
  if (src) return <img src={src} alt="" className="h-full w-full object-cover" />;
  return <>{fallback ?? initials(name)}</>;
}

// Formats acceptés par le serveur, annoncés avant l'envoi (le serveur reste seul juge du contenu).
// L'image est recadrée et réduite avant l'envoi : une grande photo d'appareil passe donc sans peine.
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const PHOTO_MAX_BYTES = 8 * 1024 * 1024;

const failure = (error: unknown, fallback: string) => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
};

/**
 * Envoi et retrait d'une photo de profil : format contrôlé, image recadrée au carré, puis annuaire
 * des photos relu pour que la nouvelle photo apparaisse partout.
 */
function usePhotoChange({
  upload,
  remove,
  onDone,
}: {
  upload: (photo: Blob) => Promise<unknown>;
  remove: () => Promise<unknown>;
  onDone?: (change: 'sent' | 'removed') => void;
}) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  // La photo d'un moniteur est aussi celle de sa fiche d'équipe, montrée dans le planning.
  const refresh = () =>
    Promise.all(
      [[PHOTO_DIRECTORY_KEY], ['team-members'], ['planning-instructors']].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
  const send = useMutation({
    mutationFn: async (file: File) => upload(await squarePhoto(file)),
    onSuccess: async () => {
      await refresh();
      onDone?.('sent');
    },
    onError: (e) => setError(failure(e, 'Envoi de la photo impossible.')),
  });
  const clear = useMutation({
    mutationFn: remove,
    onSuccess: async () => {
      await refresh();
      onDone?.('removed');
    },
    onError: (e) => setError(failure(e, 'Retrait de la photo impossible.')),
  });
  const busy = send.isPending || clear.isPending;
  const choose = (file?: File) => {
    if (!file || busy) return;
    setError(null);
    if (!PHOTO_TYPES.includes(file.type)) return setError('Format non pris en charge : JPEG, PNG ou WebP attendu.');
    if (file.size > PHOTO_MAX_BYTES) return setError('Photo trop volumineuse (8 Mo au plus).');
    send.mutate(file);
  };
  const fileInput = (
    <input
      ref={input}
      type="file"
      hidden
      accept={PHOTO_TYPES.join(',')}
      aria-label="Fichier de la photo"
      disabled={busy}
      onChange={(e) => {
        const file = e.currentTarget.files?.[0];
        e.currentTarget.value = '';
        choose(file);
      }}
    />
  );
  return {
    fileInput,
    pick: () => input.current?.click(),
    clear: () => {
      setError(null);
      clear.mutate();
    },
    busy,
    sending: send.isPending,
    error,
  };
}

const CAMERA_BUTTON =
  'absolute -bottom-0.5 -right-0.5 grid h-7 w-7 place-items-center rounded-full border-2 border-white bg-primary text-white shadow-md transition hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 disabled:opacity-60';

/**
 * Avatar qu'on peut changer : un bouton appareil photo posé dessus propose d'ajouter, de changer
 * ou de retirer la photo. L'écran affiche le message d'échec qu'il reçoit (`onError`).
 */
export function EditableAvatar({
  name,
  src,
  hasPhoto,
  upload,
  remove,
  size = 'xl',
  className,
  onDone,
  onError,
}: {
  name: string;
  src?: string;
  hasPhoto: boolean;
  upload: (photo: Blob) => Promise<unknown>;
  remove: () => Promise<unknown>;
  size?: AvatarSize;
  className?: string;
  onDone?: (change: 'sent' | 'removed') => void;
  onError?: (message: string | null) => void;
}) {
  const change = usePhotoChange({ upload, remove, onDone });
  const report = useRef(onError);
  report.current = onError;
  useEffect(() => report.current?.(change.error), [change.error]);
  return (
    <div className={cx('relative shrink-0', className)}>
      <Avatar name={name} size={size} src={src} className={change.busy ? 'opacity-60' : undefined} />
      {change.fileInput}
      {hasPhoto ? (
        <Menu
          align="start"
          trigger={(props) => (
            <button {...props} type="button" disabled={change.busy} aria-label="Photo de profil" className={CAMERA_BUTTON}>
              <Camera size={13} aria-hidden />
            </button>
          )}
          entries={[
            { label: 'Changer la photo', icon: Camera, onSelect: change.pick },
            { label: 'Retirer la photo', icon: Trash2, danger: true, onSelect: change.clear },
          ]}
        />
      ) : (
        <button type="button" disabled={change.busy} onClick={change.pick} aria-label="Ajouter une photo" className={CAMERA_BUTTON}>
          <Camera size={13} aria-hidden />
        </button>
      )}
    </div>
  );
}

/** Changer une photo de profil, avec ses boutons et ses messages (pages de réglage du compte). */
export function ProfilePhotoEditor({
  name,
  src,
  hasPhoto,
  upload,
  remove,
  hint = 'JPEG, PNG ou WebP · recadrée au carré.',
}: {
  name: string;
  src?: string;
  hasPhoto: boolean;
  upload: (photo: Blob) => Promise<unknown>;
  remove: () => Promise<unknown>;
  hint?: string;
}) {
  const change = usePhotoChange({ upload, remove });
  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <Avatar name={name} size="xl" src={src} />
        <button type="button" disabled={change.busy} onClick={change.pick} aria-label={hasPhoto ? 'Changer la photo' : 'Ajouter une photo'} className={CAMERA_BUTTON}>
          <Camera size={13} aria-hidden />
        </button>
      </div>
      <div className="flex min-w-0 flex-col gap-1.5">
        {change.fileInput}
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="secondary" icon={Camera} disabled={change.busy} onClick={change.pick}>
            {hasPhoto ? 'Changer la photo' : 'Ajouter une photo'}
          </Button>
          {hasPhoto && (
            <Button size="sm" variant="ghost" icon={Trash2} className="!text-red-500 hover:!bg-red-50" disabled={change.busy} onClick={change.clear}>
              Retirer la photo
            </Button>
          )}
        </div>
        <p className="text-[11.5px] text-muted">
          {change.busy ? (change.sending ? 'Envoi et contrôle de l’image…' : 'Retrait de la photo…') : hint}
        </p>
        {change.error && (
          <p role="alert" className="text-[12.5px] text-red-600">
            {change.error}
          </p>
        )}
      </div>
    </div>
  );
}
